import React, { useEffect, useState } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import {
  ShieldCheck, Mail, Sparkles, Eye, EyeOff, Loader2, CheckCircle2, X, LogOut,
} from "lucide-react";
import { toast } from "sonner";
import { useMemberAuth } from "../hooks/useMemberAuth";
import {
  isFoundingMember, FOUNDING_GOLD, FOUNDING_GOLD_DIM,
  FOUNDING_GOLD_GLOW_BG, FoundingBadge, foundingCallsignColor,
  foundingStarsStyle,
} from "../components/founding";
import MemberWorkbench from "../workbench/MemberWorkbench";

/**
 * Membership — public-facing page combining the subscribe CTA (anonymous → subscriber)
 * and the private member dashboard (callsign, follows, achievements, newsletter prefs,
 * suggestions). All identity stays anonymous publicly — only the member sees their
 * own email.
 */

const NEWSLETTER_OPTIONS = [
  { id: "weekly_brief", label: "Weekly Brief",
    desc: "Curated weekly digest of new investigations, source updates, and operations." },
  { id: "major_releases", label: "Major Dossier Releases",
    desc: "Notified the moment a flagship dossier (Epstein Files, Operation Ajax, etc.) ships." },
];

const MembershipPage = () => {
  const { member, loading, isMember } = useMemberAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-[#0F0F0F]">
        <Loader2 size={20} className="animate-spin text-[#A39E93]" />
      </div>
    );
  }
  // Force the callsign picker for new social signups before they enter /members.
  // Only redirect if we're actually on /members* — never bounce from other pages.
  if (isMember && member?.needs_profile_completion) {
    const from = location.pathname + location.search;
    return <Navigate to={`/auth/finish-profile?next=${encodeURIComponent(from)}`} replace />;
  }
  return (
    <div className="min-h-screen" style={{ backgroundColor: "#08070A" }}>
      <div className={`${isMember ? "max-w-6xl" : "max-w-3xl"} mx-auto px-4 sm:px-6 md:px-10 py-8 md:py-12`}>
        {isMember
          ? <MemberWorkbench member={member} />
          : <SubscribeView />}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------
// Subscribe (anonymous → subscriber)
// ---------------------------------------------------------------------

const SubscribeView = () => {
  const { subscribe } = useMemberAuth();
  const [email, setEmail] = useState("");
  const [weekly, setWeekly] = useState(true);
  const [major, setMajor] = useState(true);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await subscribe({
        email: email.trim(),
        newsletter_prefs: { weekly_brief: weekly, major_releases: major },
      });
      toast.success("Welcome to The Archive");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Subscribe failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="membership-subscribe">
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20] mb-2">
        // ANONYMOUS VISITOR → SUBSCRIBER
      </div>
      <h1 className="font-display text-3xl sm:text-4xl text-[#E4DFD3] mb-2">
        Join The Archive.
      </h1>
      <p className="text-[#A39E93] mb-6 max-w-2xl">
        Subscribe to keep your anonymous callsign across visits, follow active
        investigations, and receive curated dossier briefs. Your identity is not
        publicly visible — every member is represented by their callsign only.
      </p>

      {/* Primary path: full email + password account */}
      <div className="border border-[#2D2A26] bg-[#0a0907] p-5 max-w-xl mb-6">
        <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-[#9FA8D4] mb-3">
          // PERMANENT ACCOUNT · EMAIL + PASSWORD
        </div>
        <div className="flex flex-wrap gap-3">
          <Link to="/auth/signup"
                data-testid="subscribe-go-signup"
                className="font-mono text-[11px] uppercase tracking-[0.35em] px-4 py-2"
                style={{ background: "#D92D20", color: "#fff" }}>
            CREATE ACCOUNT
          </Link>
          <Link to="/auth/login"
                data-testid="subscribe-go-login"
                className="font-mono text-[11px] uppercase tracking-[0.35em] border px-4 py-2"
                style={{ borderColor: "#9FA8D4", color: "#9FA8D4" }}>
            SIGN IN
          </Link>
        </div>
        <p className="text-[#6F6A60] font-mono text-[10px] uppercase tracking-[0.3em] mt-3">
          Existing members: use Sign In, we&apos;ll guide a one-time password setup.
        </p>
      </div>

      {/* Legacy newsletter-only path (keeps existing subscribe UX for those who want it) */}
      <form
        onSubmit={submit}
        className="border border-[#2D2A26] bg-[#0a0907] p-5 max-w-xl"
        data-testid="membership-subscribe-form"
      >
        <label className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#A39E93]">
          Email
        </label>
        <div className="flex items-center gap-2 mt-1 border border-[#2D2A26] focus-within:border-[#D92D20] bg-[#0F0F0F] px-3 py-2">
          <Mail size={13} className="text-[#5a544a]" />
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@somewhere.org"
            data-testid="membership-subscribe-email"
            className="flex-1 bg-transparent outline-none text-[#E4DFD3] font-mono text-sm"
          />
        </div>

        <div className="mt-4 space-y-2">
          <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#A39E93]">
            Newsletter
          </div>
          <PrefRow
            checked={weekly}
            onToggle={() => setWeekly((v) => !v)}
            testid="pref-weekly"
            {...NEWSLETTER_OPTIONS[0]}
          />
          <PrefRow
            checked={major}
            onToggle={() => setMajor((v) => !v)}
            testid="pref-major"
            {...NEWSLETTER_OPTIONS[1]}
          />
        </div>

        <button
          type="submit"
          disabled={busy || !email.trim()}
          data-testid="membership-subscribe-submit"
          className="mt-5 inline-flex items-center justify-center gap-2 bg-[#D92D20] hover:bg-[#B92318] disabled:opacity-50 text-white px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.3em]"
        >
          {busy ? <Loader2 size={12} className="animate-spin" /> : <ShieldCheck size={12} />}
          Become a subscriber
        </button>

        <div className="mt-4 font-mono text-[9px] text-[#5a544a] leading-snug">
          // we never display your email publicly. members are identified by
          callsign only. you can unsubscribe at any time from your profile.
        </div>
      </form>
    </div>
  );
};

const PrefRow = ({ checked, onToggle, label, desc, testid }) => (
  <button
    type="button"
    onClick={onToggle}
    data-testid={testid}
    className={
      "w-full text-left border p-2.5 flex items-start gap-3 transition " +
      (checked ? "border-[#5DD4B3] bg-[#0c1a0e]" : "border-[#2D2A26] hover:border-[#454038]")
    }
  >
    <div
      className={
        "shrink-0 w-4 h-4 mt-0.5 border flex items-center justify-center " +
        (checked ? "bg-[#5DD4B3] border-[#5DD4B3]" : "border-[#454038]")
      }
    >
      {checked && <CheckCircle2 size={10} className="text-[#0F0F0F]" />}
    </div>
    <div>
      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#E4DFD3]">
        {label}
      </div>
      <div className="text-[11px] text-[#A39E93] mt-0.5">{desc}</div>
    </div>
  </button>
);

// ---------------------------------------------------------------------
// Member dashboard (private profile)
// ---------------------------------------------------------------------

const MemberDashboard = ({ member }) => {
  const { patchMe, fetchSuggestions, logout } = useMemberAuth();
  const [showEmail, setShowEmail] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  useEffect(() => {
    fetchSuggestions().then(setSuggestions).catch(() => setSuggestions([]));
  }, [fetchSuggestions]);

  const toggleNewsletterPref = async (key) => {
    const prefs = { ...(member.newsletter_prefs || {}), [key]: !member.newsletter_prefs?.[key] };
    try {
      await patchMe({ newsletter_prefs: prefs });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const followItem = async (s) => {
    try {
      if (s.type === "topic") {
        const next = Array.from(new Set([...(member.followed_topics || []), s.slug || s.id]));
        await patchMe({ followed_topics: next });
      } else {
        const next = Array.from(new Set([...(member.followed_ef_entities || []), s.id]));
        await patchMe({ followed_ef_entities: next });
      }
      toast.success(`Following ${s.title}`);
      setSuggestions((p) => p.filter((x) => x.id !== s.id));
    } catch (e) {
      toast.error("Follow failed");
    }
  };

  const unfollowTopic = async (slug) => {
    const next = (member.followed_topics || []).filter((x) => x !== slug);
    await patchMe({ followed_topics: next });
  };

  const unfollowEntity = async (id) => {
    const next = (member.followed_ef_entities || []).filter((x) => x !== id);
    await patchMe({ followed_ef_entities: next });
  };

  return (
    <div data-testid="membership-dashboard">
      {(() => {
        const isFounding = isFoundingMember(member);
        const stars = Math.max(0, Math.min(5, Number(member.clearance_level || 0)));
        return (
          <div
            className="border p-4 sm:p-5 mb-4 transition-colors"
            style={isFounding
              ? {
                  borderColor: FOUNDING_GOLD_DIM,
                  backgroundColor: FOUNDING_GOLD_GLOW_BG,
                  boxShadow: "inset 0 0 0 1px rgba(224, 165, 38, 0.10)",
                }
              : { borderColor: "#2D2A26", backgroundColor: "#0a0907" }}
            data-testid="member-identity-card"
          >
            {isFounding && (
              <div className="mb-3 flex items-center justify-between gap-3 flex-wrap">
                <FoundingBadge size="md" withGrantLine testid="member-founding-badge" />
                {stars > 0 && (
                  <span
                    className="font-mono text-sm tracking-wider"
                    style={foundingStarsStyle(true)}
                    aria-label={`Clearance level ${stars} of 5`}
                    data-testid="member-founding-stars"
                  >
                    {"★".repeat(stars)}{"☆".repeat(5 - stars)}
                  </span>
                )}
              </div>
            )}
            <div className="flex items-end justify-between flex-wrap gap-3">
              <div>
                <div
                  className="font-mono text-[10px] uppercase tracking-[0.3em] mb-1"
                  style={{ color: isFounding ? FOUNDING_GOLD : "#D92D20" }}
                >
                  // SUBSCRIBER PROFILE · PRIVATE
                </div>
                <h1
                  className="font-display text-3xl sm:text-4xl"
                  style={{ color: foundingCallsignColor(isFounding) }}
                  data-testid="member-callsign"
                >
                  {member.callsign || "—"}
                </h1>
                <div className="font-mono text-[10px] text-[#A39E93] mt-1">
                  Joined {(member.joined_at || "").slice(0, 10)} · Tier {(member.tier || "subscriber").toUpperCase()}
                  {isFounding && (
                    <span
                      className="ml-2"
                      style={{ color: FOUNDING_GOLD }}
                      data-testid="member-designation-line"
                    >
                      · Founding Member
                    </span>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={logout}
                data-testid="member-logout"
                className="inline-flex items-center gap-1.5 border border-[#454038] hover:border-[#D92D20] text-[#A39E93] px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em]"
              >
                <LogOut size={11} /> Sign out
              </button>
            </div>
          </div>
        );
      })()}

      {/* Email row */}
      <div className="border border-[#2D2A26] bg-[#0a0907] p-3 mb-3 flex items-center gap-3 flex-wrap">
        <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#A39E93]">Email</span>
        <span className="font-mono text-sm text-[#E4DFD3]" data-testid="member-email">
          {showEmail ? member.email : "••••••••••@••••••"}
        </span>
        <button
          type="button"
          onClick={() => setShowEmail((v) => !v)}
          data-testid="member-toggle-email"
          className="ml-auto inline-flex items-center gap-1.5 border border-[#454038] hover:border-[#D92D20] text-[#A39E93] px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em]"
        >
          {showEmail ? <EyeOff size={11} /> : <Eye size={11} />}
          {showEmail ? "Hide" : "Reveal"}
        </button>
      </div>

      {/* Achievements */}
      <Section title="Achievements" testid="member-achievements">
        {(member.achievements || []).length === 0 ? (
          <Empty>None yet — follow more investigations to unlock badges.</Empty>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {member.achievements.map((a) => (
              <span
                key={a}
                className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#E0A526] border border-[#E0A526]/50 px-2 py-0.5"
              >
                {a}
              </span>
            ))}
          </div>
        )}
      </Section>

      {/* Followed topics */}
      <Section title="Followed Topics" testid="member-followed-topics">
        {(member.followed_topics || []).length === 0 ? (
          <Empty>Not following any topics yet.</Empty>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {member.followed_topics.map((t) => (
              <FollowChip key={t} label={t} onRemove={() => unfollowTopic(t)} />
            ))}
          </div>
        )}
      </Section>

      {/* Followed entities */}
      {(member.followed_ef_entities || []).length > 0 && (
        <Section title="Followed Epstein Entities" testid="member-followed-entities">
          <div className="flex flex-wrap gap-1.5">
            {member.followed_ef_entities.map((id) => (
              <FollowChip key={id} label={id} onRemove={() => unfollowEntity(id)} />
            ))}
          </div>
        </Section>
      )}

      {/* Suggestions */}
      <Section title="Suggested for you" icon={<Sparkles size={11} className="text-[#E0A526]" />} testid="member-suggestions">
        {suggestions.length === 0 ? (
          <Empty>No suggestions right now — check back soon.</Empty>
        ) : (
          <div className="space-y-1.5">
            {suggestions.map((s) => (
              <div
                key={`${s.type}-${s.id}`}
                data-testid={`suggestion-${s.id}`}
                className="flex items-center gap-2 border border-[#2D2A26] bg-[#0F0F0F] p-2"
              >
                <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#9FA8D4] border border-[#9FA8D4]/40 px-1.5 py-0.5">
                  {s.type === "topic" ? "TOPIC" : "EF"}
                </span>
                <span className="text-sm text-[#E4DFD3] truncate flex-1">{s.title}</span>
                <button
                  type="button"
                  onClick={() => followItem(s)}
                  data-testid={`suggestion-follow-${s.id}`}
                  className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#5DD4B3] border border-[#5DD4B3]/60 px-2 py-0.5 hover:bg-[#0c1a0e]"
                >
                  + follow
                </button>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Newsletter */}
      <Section title="Newsletter Preferences" testid="member-newsletter-prefs">
        <div className="space-y-2">
          {NEWSLETTER_OPTIONS.map((o) => (
            <PrefRow
              key={o.key}
              checked={!!member.newsletter_prefs?.[o.key]}
              onToggle={() => toggleNewsletterPref(o.key)}
              testid={`profile-pref-${o.key}`}
              {...o}
            />
          ))}
        </div>
      </Section>
    </div>
  );
};

const Section = ({ title, icon, children, testid }) => (
  <div className="border border-[#2D2A26] bg-[#0a0907] p-3 mb-3" data-testid={testid}>
    <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#A39E93] mb-2 flex items-center gap-1.5">
      {icon}{title}
    </div>
    {children}
  </div>
);

const Empty = ({ children }) => (
  <div className="font-mono text-[10px] text-[#757065]">{children}</div>
);

const FollowChip = ({ label, onRemove }) => (
  <span className="inline-flex items-center gap-1 font-mono text-[10px] text-[#E4DFD3] border border-[#2D2A26] bg-[#0F0F0F] pl-2 pr-1 py-0.5">
    {label}
    <button
      type="button"
      onClick={onRemove}
      className="inline-flex items-center justify-center w-4 h-4 text-[#757065] hover:text-[#D92D20]"
      aria-label={`Unfollow ${label}`}
    >
      <X size={9} />
    </button>
  </span>
);

export default MembershipPage;
